import { useMemo } from "react"

import * as incidentsApi from "@/api/useIncidents"
import { App } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { predicateHelpers } from "@/hooks/useHttp"
import { useLogger } from "@/hooks/useLogger"
import { useQueryString } from "@/hooks/useQueryString"
import { useServices } from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import * as filtersConsts from "@/pages/TriagePage/const/filters.constants"
import {
  DISMISSED_TAB,
  INCIDENTS_TAB
} from "@/pages/TriagePage/const/filters.constants"
import { useDataContext } from "@/pages/TriagePage/hooks/useData"
import {
  filteredDemoData,
  getDemoAiRules,
  getDemoData,
  RULE_CATEGORY_AI
} from "./demo"
import {
  getAdrIncidents,
  getAdrIncidentsHistory,
  getEnvironmentsForAdrIncidents,
  getListAdrIncidents,
  getRulesForAdrIncidents
} from "./useAdrIncidents.selector"

import type {
  AdrIncidentsResponse,
  IncidentsHistoryResponse
} from "@/api/useIncidents.types"
import type { Config, Params } from "@/hooks/useHttp"

export const useAdrIncidentsList = (config?: { enabled?: boolean }) => {
  const keysFilters = [
    filtersConsts.PRIORITY_FILTERS_KEY,
    filtersConsts.ENVIRONMENTS_FILTERS_KEY,
    filtersConsts.TYPE_FILTERS_KEY,
    filtersConsts.IS_NEW_FILTERS_KEY
  ]
  const { tab, pageNumber, ...params } = useQueryString([
    filtersConsts.TAB_KEY,
    ...keysFilters
  ]) as { tab?: string; pageNumber?: string }
  const dismissed = tab === DISMISSED_TAB
  const apiParams: Record<string, unknown> = {
    ...params,
    dismissed
  }
  const demoApiParams = { ...apiParams }

  if (typeof apiParams[filtersConsts.TYPE_FILTERS_KEY] === "string")
    apiParams[filtersConsts.TYPE_FILTERS_KEY] = apiParams[
      filtersConsts.TYPE_FILTERS_KEY
    ]
      .split(",")
      .filter((v) => v !== RULE_CATEGORY_AI)
      .join(",")

  const ONE_MINUTE = 60 * 1000 // 1 minute
  const enabled = config?.enabled
  const commonConfig = {
    enabled,
    staleTime: ONE_MINUTE,
    refetchInterval: ONE_MINUTE
  }

  const rulesContext = incidentsApi.useGetRules(commonConfig)
  const environmentContext = incidentsApi.useGetEnvironments(commonConfig)
  const {
    isLoading: _,
    data,
    ...context
  } = incidentsApi.useLoadMoreAdrIncidents<AdrIncidentsResponse["data"]>(
    apiParams,
    { initialPageParam: 0, ...commonConfig }
  )
  const { auth } = useServices()
  const isDemo = auth.isDemo()
  const dataContext = useDataContext()

  const incidentDependency = isDemo && dataContext.incidents
  const paramsDependency = isDemo && JSON.stringify(demoApiParams)
  const response: ReturnType<typeof getListAdrIncidents> = useMemo(() => {
    if (!isDemo) return getListAdrIncidents(data)

    const currentData = dataContext.incidents || getDemoData()

    return getListAdrIncidents({
      pageParams: [0],
      pages: [
        dataContext.incidents.filter((incident) => {
          return filteredDemoData(incident, demoApiParams, {
            rules: rulesContext?.response?.data
          })
        })
      ],
      metadata: {
        total: currentData.length,
        page: 1,
        size: 100,
        hasNext: false
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDemo, incidentDependency, paramsDependency])

  const isLoading =
    _ || rulesContext.isLoading || environmentContext.isLoading || !enabled

  return {
    isLoading,
    response,
    currentTab: dismissed ? DISMISSED_TAB : INCIDENTS_TAB,
    noIncidents:
      !response?.data?.list?.length &&
      !keysFilters.some((key) => (params as any)?.[key]),
    ...context
  }
}

export const useGetAdrIncidentsHistory = (
  params?: Params,
  config?: Config<any>
) => {
  const { auth } = useServices()
  const isDemo = auth.isDemo()
  const context = incidentsApi.useGetIncidentsHistory(params, {
    select: isDemo
      ? (res: IncidentsHistoryResponse) => getAdrIncidentsHistory(res, isDemo)
      : getAdrIncidentsHistory,
    ...config
  })

  return context
}

export const useAdrIncident = (id: string) => {
  const { auth } = useServices()
  const isDemo = auth.isDemo()

  if (isDemo) {
    const data = getDemoData().find((item) => item.incident.general.id === id)

    if (!data) return { isLoading: true, response: undefined }

    return {
      isLoading: false,
      response: getAdrIncidents({
        data,
        metadata: {
          total: 1,
          page: 1,
          size: 100,
          hasNext: false
        }
      })
    }
  }

  const context = incidentsApi.useGetAdrIncident(id, {
    enabled: !!id && !isDemo,
    select: getAdrIncidents
  })

  return context
}

export const useDismissIncident = (props: { onClose?: () => void } = {}) => {
  const context = incidentsApi.useDismissIncident(
    undefined,
    undefined,
    (queryClient) => ({
      onSuccess: () => {
        return queryClient.refetchQueries({
          predicate: (query) =>
            predicateHelpers.includesKeysPredicate([
              RESOURCES_NAMES.INCIDENTS.LIST
            ])(query) ||
            predicateHelpers.includesKeysPredicate([
              RESOURCES_NAMES.INCIDENTS.HISTORY
            ])(query)
        })
      }
    })
  )
  const { logger } = useLogger()
  const { t } = useTranslation()
  const { message } = App.useApp()

  const dismissIncident = async (
    incidentId: string,
    data: { reason?: string }
  ) => {
    const key = "dismiss-incident"

    try {
      if (props.onClose)
        message.open({
          key,
          duration: 0,
          type: "loading",
          content: t("triage.dismissing", {
            entityType: t("general.incident", { count: 1 })
          })
        })

      await context.mutateAsync({
        incidentId,
        needle: `/${incidentId}`,
        data: { reason: data.reason }
      })

      if (props.onClose) {
        message.destroy(key)
        props.onClose()
      }

      message.success(
        t("triage.dismiss_success", {
          count: 1,
          entityType: t("general.incident", { count: 1 })
        })
      )

      return Promise.resolve()
    } catch (error) {
      if (props.onClose) message.destroy(key)
      // Show error message
      message.error("Failed to dismiss incident")

      // Log error
      logger.error(error as Error, {
        tags: { custom_error: "ERROR_DISMISS_INCIDENT" }
      })

      return Promise.reject(error)
    }
  }

  return { dismissIncident, isLoading: context.isPending }
}

export const useRulesForAdrIncident = (config: { enabled: boolean }) => {
  const { auth } = useServices()
  const isDemo = auth.isDemo()
  const dataContext = useDataContext()

  const context = incidentsApi.useGetRules({
    enabled: config.enabled,
    select: isDemo
      ? (response) => {
          return getRulesForAdrIncidents({
            ...response,
            data: [
              ...response.data.map((rule) => ({
                ...rule,
                detectedIncidentsCount:
                  rule.detectedIncidentsCount +
                  (dataContext.rules[rule.id] || 0)
              })),
              ...getDemoAiRules(dataContext.rules)
            ]
          })
        }
      : getRulesForAdrIncidents
  })

  return context
}

export const useEnvironmentsForAdrIncident = (config: { enabled: boolean }) => {
  const { auth } = useServices()
  const isDemo = auth.isDemo()
  const dataContext = useDataContext()

  const context = incidentsApi.useGetEnvironments<
    ReturnType<typeof getEnvironmentsForAdrIncidents>
  >({
    enabled: config.enabled,
    select: isDemo
      ? (response) => {
          return getEnvironmentsForAdrIncidents({
            ...response,
            data: response.data.map((environment) => {
              return {
                ...environment,
                isMonitoringAdr:
                  environment.name === "production" ||
                  environment.name === "demo"
                    ? true
                    : environment.isMonitoringAdr,
                detectedIncidentsCount:
                  (environment.detectedIncidentsCount || 0) +
                  (dataContext.environments[environment.name] || 0)
              }
            })
          })
        }
      : getEnvironmentsForAdrIncidents
  })

  return context
}
